import React from 'react';
import * as yup from 'yup';
import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
  OverlayInteractionMode,
} from '$/components/Interactions/types';
import { StyleProps } from '$/components/ui';
import {
  AttendeeFragment,
  InteractionDetails,
  InteractionOverlaySettings,
  InteractionForAttendeeFragment,
  InteractionFragment,
  PublicEWebinarFragment,
} from '$/GraphQL/schema/schema';
import formatTimeInWebinarSecs from '$/utils/formatTimeInWebinarSecs';
import { yupIsValidUrl } from '$/utils/isUrl';
import { OverlayInteractionInfo } from '../OverlayInteractionInfo';
import {
  InteractionInfo,
  OverlayComponentProps,
  OverlayDimensions,
  VideoOverlayTypes,
} from '../InteractionInfo';
import { theme } from 'twin.macro';
import { TimelineComponents } from '$/components/WebinarPlayer/context';
import { ConversionNotificationsOverlay } from './ConversionNotificationsCard';
import { useConversionNotificationCalcDimensions } from './useConversionNotificationCalcDimensions';
import { ConversionNotificationsIcon } from '$/images/FontAwesomeIcons';
import { AnalyticsCardProps } from '../cards/types';
import ConversionNotificationsSettings from './ConversionNotificationsSettings';

const AnalyticsClickCard = React.lazy(() => import('../cards/AnalyticsClickCard'));
const ConversionNotificationsModal = React.lazy(() => import('./ConversionNotificationsModal'));

export class ConversionNotificationsInfo extends OverlayInteractionInfo {
  public constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
  }

  public typeName(): string | undefined {
    return `Conversion alerts`;
  }

  public color(): string | undefined {
    return theme`colors.green.400`.toString();
  }

  public timelineComponent(): keyof TimelineComponents {
    return 'conversions';
  }

  public detailsDefault(interactions: InteractionInfo[]): InteractionDetails {
    return {
      ...super.detailsDefault(interactions),
      title: this.title(),
      description: this.description(),
    };
  }

  public icon(): React.FC<StyleProps> {
    return ConversionNotificationsIcon;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      ...super.modalValidationRules(),
      endsAt: yup
        .number()
        .typeError('Time must be specified in -HH:MM:SS or -MM:SS format.')
        .required()
        .when('playbackPosition', ([playbackPosition]: number[], schema: yup.NumberSchema) => {
          if (!this.ewebinar) return schema;

          const maxDuration = this.ewebinar.duration + this.ewebinar.exitRoomDurationSecs + 1; // TODO: +1: because we add 1 seconds into ewebinarDuration in Rooms.tsx
          const minDuration = Math.max(
            this.ewebinar.waitingRoomDurationSecs * -1,
            playbackPosition
          );
          schema = schema.min(
            minDuration,
            `Minimum position is ${formatTimeInWebinarSecs(minDuration)}`
          );
          schema = schema.max(
            maxDuration,
            `Maximum position is ${formatTimeInWebinarSecs(maxDuration)}`
          );
          return schema;
        }),
      link: yupIsValidUrl(),
    };
  }

  public isInteractiveOverlay(): boolean {
    return true;
  }

  public isInteractiveCard(): boolean {
    return false;
  }

  public hasCard(): boolean {
    return false;
  }

  public hasVideoOverlay(): VideoOverlayTypes | null {
    return 'over-area';
  }

  public hasPropertyNameField(): boolean {
    return false;
  }

  public hasEngagement(): boolean {
    return false;
  }

  public staticOverlayPosition(): boolean {
    return true;
  }

  public renderOverlayComponent(): React.FC<OverlayComponentProps> {
    return ConversionNotificationsOverlay;
  }

  public calcOverlayDimensionsHook(): (
    interaction: InteractionInfo,
    playerWidth: number,
    playerHeight: number,
    settings?: InteractionOverlaySettings | null
  ) => OverlayDimensions {
    return useConversionNotificationCalcDimensions;
  }

  public displayLocation(): 'top' | 'bottom' {
    return this.i.details?.overlaySettings?.position?.[1] === 0 ? 'top' : 'bottom';
  }

  public onlyOneInstance(): boolean {
    return true;
  }

  public renderNotification(_mode?: OverlayInteractionMode): JSX.Element | null {
    return null;
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return ConversionNotificationsModal;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook {
    return ConversionNotificationsSettings;
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsClickCard;
  }
}
